/**
 * ReviewModerationPanel Component
 * Admin panel for moderating preset reviews
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  Trash2,
  Flag,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  User,
  Star,
  MessageSquare,
  Clock,
  MoreVertical,
  Ban,
  RefreshCw,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Moderation status badge
const StatusBadge = ({ status }) => {
  const configs = {
    pending: { icon: Clock, color: 'text-amber-400 bg-amber-500/10 border-amber-500/20', label: 'Pending' },
    approved: { icon: CheckCircle, color: 'text-green-400 bg-green-500/10 border-green-500/20', label: 'Approved' },
    rejected: { icon: XCircle, color: 'text-red-400 bg-red-500/10 border-red-500/20', label: 'Rejected' },
    flagged: { icon: Flag, color: 'text-orange-400 bg-orange-500/10 border-orange-500/20', label: 'Flagged' },
  };

  const config = configs[status] || configs.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// Star display component
const StarDisplay = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`w-3 h-3 ${
          star <= rating ? 'fill-amber-400 text-amber-400' : 'fill-zinc-600 text-zinc-600'
        }`}
      />
    ))}
  </div>
);

// Review card for moderation
const ModerationReviewCard = ({ review, onApprove, onReject, onDelete, onViewDetails }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const formattedDate = new Date(review.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-800/50 rounded-xl border border-zinc-700 overflow-hidden"
    >
      {/* Header */}
      <div className="p-4 flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-zinc-600 to-zinc-700 flex items-center justify-center shrink-0">
            <User className="w-5 h-5 text-zinc-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-zinc-200">{review.user_name}</span>
              <StatusBadge status={review.moderation_status || 'pending'} />
              {review.verified_purchase && (
                <span className="text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">
                  Verified
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-1">
              <StarDisplay rating={review.rating} />
              <span className="text-xs text-zinc-500">{formattedDate}</span>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              Preset: <span className="text-zinc-400">{review.preset_name}</span>
            </p>
          </div>
        </div>

        {/* Actions menu */}
        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-2 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700 rounded-lg transition-colors"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          
          <AnimatePresence>
            {showActions && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="absolute right-0 top-full mt-1 w-40 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-10 overflow-hidden"
              >
                <button
                  onClick={() => { onViewDetails(review); setShowActions(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  View Details
                </button>
                <button
                  onClick={() => { onApprove(review.id); setShowActions(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-green-400 hover:bg-zinc-700 flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Approve
                </button>
                <button
                  onClick={() => { onReject(review.id); setShowActions(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-orange-400 hover:bg-zinc-700 flex items-center gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  Reject
                </button>
                <button
                  onClick={() => { onDelete(review.id); setShowActions(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-zinc-700 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Review content */}
      <div className="px-4 pb-4">
        {review.title && (
          <h4 className="font-medium text-zinc-200 mb-1">{review.title}</h4>
        )}
        <p className={`text-sm text-zinc-400 ${!isExpanded && 'line-clamp-2'}`}>
          {review.review_text || '(No review text)'}
        </p>
        {review.review_text && review.review_text.length > 150 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs text-violet-400 hover:text-violet-300 mt-1"
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>

      {/* Quick actions */}
      <div className="px-4 py-3 bg-zinc-800/50 border-t border-zinc-700/50 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <MessageSquare className="w-3 h-3" />
          <span>{review.helpful_count || 0} found helpful</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onApprove(review.id)}
            className="text-green-400 hover:bg-green-500/10"
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            Approve
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onReject(review.id)}
            className="text-red-400 hover:bg-red-500/10"
          >
            <XCircle className="w-4 h-4 mr-1" />
            Reject
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Main ReviewModerationPanel component
const ReviewModerationPanel = ({ isAdmin = false }) => {
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected, flagged
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    flagged: 0,
  });
  const [selectedReview, setSelectedReview] = useState(null);

  const fetchReviews = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(filter !== 'all' && { status: filter }),
        ...(searchQuery && { search: searchQuery }),
      });

      const response = await fetch(`${API_URL}/api/admin/reviews?${params}`);
      if (!response.ok) throw new Error('Failed to fetch reviews');

      const data = await response.json();
      setReviews(data.reviews || []);
      setTotalPages(Math.ceil((data.total || 0) / 20));
      setStats(data.stats || stats);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      // For demo, show sample data
      setReviews([
        {
          id: 'review_demo_1',
          preset_id: 'preset_1',
          preset_name: 'Heavy Metal Crunch',
          user_id: 'user_1',
          user_name: 'MetalGuitarist',
          rating: 5,
          title: 'Best metal tone ever!',
          review_text: 'This preset nails the modern metal sound. Perfect for djent and progressive metal. The low end is tight and the highs cut through the mix.',
          helpful_count: 12,
          moderation_status: 'pending',
          verified_purchase: true,
          created_at: new Date().toISOString(),
        },
        {
          id: 'review_demo_2',
          preset_id: 'preset_2',
          preset_name: 'Clean Jazz',
          user_id: 'user_2',
          user_name: 'JazzMan42',
          rating: 4,
          title: 'Great clean tone',
          review_text: 'Nice warm clean tone. Could use a bit more sparkle in the highs but overall very usable for jazz and blues.',
          helpful_count: 5,
          moderation_status: 'pending',
          verified_purchase: false,
          created_at: new Date(Date.now() - 86400000).toISOString(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [page, filter, searchQuery]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleApprove = async (reviewId) => {
    try {
      // API call would go here
      setReviews(reviews.map(r => 
        r.id === reviewId ? { ...r, moderation_status: 'approved' } : r
      ));
      toast.success('Review approved');
    } catch (error) {
      toast.error('Failed to approve review');
    }
  };

  const handleReject = async (reviewId) => {
    try {
      setReviews(reviews.map(r => 
        r.id === reviewId ? { ...r, moderation_status: 'rejected' } : r
      ));
      toast.success('Review rejected');
    } catch (error) {
      toast.error('Failed to reject review');
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review? This cannot be undone.')) {
      return;
    }
    try {
      setReviews(reviews.filter(r => r.id !== reviewId));
      toast.success('Review deleted');
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const handleBulkAction = async (action) => {
    const pendingReviews = reviews.filter(r => r.moderation_status === 'pending');
    if (pendingReviews.length === 0) {
      toast.info('No pending reviews to process');
      return;
    }

    const confirmMsg = action === 'approve' 
      ? `Approve all ${pendingReviews.length} pending reviews?`
      : `Reject all ${pendingReviews.length} pending reviews?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      setReviews(reviews.map(r => 
        r.moderation_status === 'pending' 
          ? { ...r, moderation_status: action === 'approve' ? 'approved' : 'rejected' }
          : r
      ));
      toast.success(`${pendingReviews.length} reviews ${action}d`);
    } catch (error) {
      toast.error('Failed to process reviews');
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center p-12 text-zinc-500">
        <div className="text-center">
          <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Admin access required</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-zinc-100 flex items-center gap-2">
            <Shield className="w-6 h-6 text-violet-400" />
            Review Moderation
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            Manage and moderate user reviews
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchReviews}
            className="border-zinc-700"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-zinc-700"
          >
            <Download className="w-4 h-4 mr-1" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total', value: stats.total, color: 'text-zinc-400', bg: 'bg-zinc-800/50' },
          { label: 'Pending', value: stats.pending, color: 'text-amber-400', bg: 'bg-amber-500/10' },
          { label: 'Approved', value: stats.approved, color: 'text-green-400', bg: 'bg-green-500/10' },
          { label: 'Rejected', value: stats.rejected, color: 'text-red-400', bg: 'bg-red-500/10' },
          { label: 'Flagged', value: stats.flagged, color: 'text-orange-400', bg: 'bg-orange-500/10' },
        ].map((stat) => (
          <div
            key={stat.label}
            className={`p-3 rounded-lg ${stat.bg} border border-zinc-700/50`}
          >
            <p className="text-xs text-zinc-500">{stat.label}</p>
            <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filters and search */}
      <div className="flex flex-col md:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            placeholder="Search reviews by user, preset, or content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-200 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>

        {/* Filter dropdown */}
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-300 focus:outline-none focus:ring-2 focus:ring-violet-500"
        >
          <option value="all">All Reviews</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="flagged">Flagged</option>
        </select>

        {/* Bulk actions */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => handleBulkAction('approve')}
            className="bg-green-600 hover:bg-green-700"
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            Approve All
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => handleBulkAction('reject')}
          >
            <XCircle className="w-4 h-4 mr-1" />
            Reject All
          </Button>
        </div>
      </div>

      {/* Reviews list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-violet-400" />
        </div>
      ) : reviews.length > 0 ? (
        <div className="space-y-4">
          {reviews.map((review) => (
            <ModerationReviewCard
              key={review.id}
              review={review}
              onApprove={handleApprove}
              onReject={handleReject}
              onDelete={handleDelete}
              onViewDetails={setSelectedReview}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-zinc-500">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No reviews found</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="border-zinc-700"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-zinc-400">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="border-zinc-700"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Review detail modal */}
      <AnimatePresence>
        {selectedReview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedReview(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-lg w-full max-h-[80vh] overflow-auto"
            >
              <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
                <h3 className="font-semibold text-zinc-200">Review Details</h3>
                <button
                  onClick={() => setSelectedReview(null)}
                  className="p-1 text-zinc-500 hover:text-zinc-300"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <p className="text-xs text-zinc-500 mb-1">User</p>
                  <p className="text-zinc-200">{selectedReview.user_name}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Preset</p>
                  <p className="text-zinc-200">{selectedReview.preset_name}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Rating</p>
                  <StarDisplay rating={selectedReview.rating} />
                </div>
                {selectedReview.title && (
                  <div>
                    <p className="text-xs text-zinc-500 mb-1">Title</p>
                    <p className="text-zinc-200">{selectedReview.title}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Review</p>
                  <p className="text-zinc-300">{selectedReview.review_text || '(No text)'}</p>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => { handleApprove(selectedReview.id); setSelectedReview(null); }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={() => { handleReject(selectedReview.id); setSelectedReview(null); }}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReviewModerationPanel;
export { StatusBadge, ModerationReviewCard };
